// Business-Wetter — Lage-Score 0–100 + 16 Wetterlagen (AUFTRAG K2,
// docs/COCKPIT-SANIERUNG.md Runden 4–6 + COCKPIT-MASTERPLAN §2).
//
// Der Lage-Score ist DAS zentrale Stimmungs-Signal des Cockpits
// (Kohärenz-Regel Nr. 1): der ZenFokus-Baum liest ihn, nichts definiert
// eigene Schwellen. Pure functions (Stil derive-actions) — NUR aus
// bereits geladenen Dashboard-Queries, vitest-bar.

import type { FreshnessResponse, MahnungenEingegangenResponse } from "./api";
import type { Netto4W, TodayAction } from "./derive-actions";

export type WetterFamilie = "calm" | "mild" | "auffrischend" | "sturm" | "orkan";

export interface WetterFaktoren {
  /** 0–100 je Faktor (bereits gewichtet aufsummiert ergibt score). */
  liquiditaet: number; // 30 %
  forderungsdruck: number; // 20 %
  handlungsdruck: number; // 20 %
  gegenwind: number; // 15 %
  sicht: number; // 15 %
}

export interface WetterScore {
  score: number; // 0–100
  familie: WetterFamilie;
  faktoren: WetterFaktoren;
  /** Faktor mit dem höchsten gewichteten Beitrag — wählt die Lage. */
  dominanterFaktor: keyof WetterFaktoren;
}

export interface DeriveWetterInput {
  netto: Netto4W;
  ueberfaelligEur: number;
  actions: TodayAction[];
  mahnungen?: MahnungenEingegangenResponse | null;
  freshness?: FreshnessResponse | null;
  /** Cash-Graph-Reichweite (Tage) — koppelt die Liquiditäts-Enge an den
   *  Runway, nicht nur an Netto-4W. Kurzer Runway = Sturm, auch wenn das
   *  4-Wochen-Fenster noch grün aussieht. */
  reichweiteTage?: number | null;
  /** Gruppen-Umsatz-Pace vs. Vorjahr (1.0 = auf Kurs). Unter Plan = mehr
   *  Gegenwind. So spiegelt der Baum auch die Umsatz-Gesamtlage. */
  gruppenPace?: number | null;
}

const GEWICHTE: Record<keyof WetterFaktoren, number> = {
  liquiditaet: 0.3,
  forderungsdruck: 0.2,
  handlungsdruck: 0.2,
  gegenwind: 0.15,
  sicht: 0.15,
};

const clamp = (v: number, lo = 0, hi = 100): number => Math.min(hi, Math.max(lo, v));

function familieFor(score: number): WetterFamilie {
  if (score < 20) return "calm";
  if (score < 40) return "mild";
  if (score < 60) return "auffrischend";
  if (score < 80) return "sturm";
  return "orkan";
}

export function deriveWetter(input: DeriveWetterInput): WetterScore {
  // Liquiditäts-Enge = STÄRKERES Signal aus Netto-4W-Puffer UND Runway
  // (Gesamtlage, nicht nur das 4-Wochen-Fenster).
  // Netto: 0 Punkte ab +20k Puffer, 100 ab −20k.
  const netto = input.netto.netto_eur;
  const nettoStress = netto == null ? 50 : clamp(((20_000 - netto) / 40_000) * 100);
  // Runway: 0 Punkte ab 120 Tagen, 100 bei 0 Tagen (linear). null → neutral.
  const reichweite = input.reichweiteTage;
  const runwayStress =
    reichweite == null ? nettoStress : clamp(((120 - reichweite) / 120) * 100);
  const liquiditaet = Math.max(nettoStress, runwayStress);

  // Forderungsdruck: überfällig € vs. 50k (Manometer-Schwelle).
  const forderungsdruck = clamp((input.ueberfaelligEur / 50_000) * 100);

  // Handlungsdruck: critical 25, warn 10, cap 100.
  const handlungsdruck = clamp(
    input.actions.reduce(
      (sum, a) => sum + (a.severity === "critical" ? 25 : a.severity === "warn" ? 10 : 0),
      0,
    ),
  );

  // Gegenwind: Mahnungen gegen uns (Anzahl + €/10k) PLUS Umsatz-Rückstand
  // ggü. Vorjahr — eine schwache Umsatz-Gesamtlage frischt den Wind auf.
  const mahnCount = input.mahnungen?.n_offen ?? 0;
  const mahnEur = Number.parseFloat(input.mahnungen?.total_offen_eur ?? "0") || 0;
  const pace = input.gruppenPace;
  const paceGegenwind = pace == null || pace >= 1 ? 0 : clamp((1 - pace) * 180);
  const gegenwind = clamp(mahnCount * 15 + (mahnEur / 10_000) * 10 + paceGegenwind);

  // Sicht: je stale/missing-Strecke 25 (Nebel = Blindflug).
  const gestoert = (input.freshness?.items ?? []).filter(
    (i) => i.status === "stale" || i.status === "missing",
  ).length;
  const sicht = clamp(gestoert * 25);

  const faktoren: WetterFaktoren = {
    liquiditaet,
    forderungsdruck,
    handlungsdruck,
    gegenwind,
    sicht,
  };
  const score = clamp(
    Math.round(
      (Object.keys(GEWICHTE) as (keyof WetterFaktoren)[]).reduce(
        (sum, k) => sum + faktoren[k] * GEWICHTE[k],
        0,
      ),
    ),
  );

  let dominanterFaktor: keyof WetterFaktoren = "liquiditaet";
  let max = -1;
  for (const k of Object.keys(GEWICHTE) as (keyof WetterFaktoren)[]) {
    const beitrag = faktoren[k] * GEWICHTE[k];
    if (beitrag > max) {
      max = beitrag;
      dominanterFaktor = k;
    }
  }

  return { score, familie: familieFor(score), faktoren, dominanterFaktor };
}

// ----------------------------------------------------------------------
// 16 Wetterlagen (5. Runde) — der DOMINANTE Faktor wählt die Lage in der
// Familie, Tages-Seed entscheidet bei Gleichstand.
// ----------------------------------------------------------------------

export type WetterLageId =
  | "windstille"
  | "morgentau"
  | "sonnenstrahlen"
  | "leichte_brise"
  | "frischer_wind"
  | "vogelflug"
  | "wolkenschatten"
  | "falter"
  | "boeig"
  | "erste_tropfen"
  | "nebelbank"
  | "blaetterwirbel"
  | "sturm"
  | "platzregen"
  | "gewitterleuchten"
  | "orkan";

export interface WetterLage {
  id: WetterLageId;
  familie: WetterFamilie;
  /** Wind-Parameter für die Baum-Engine (setzt NUR amp/gust — Engine bleibt eine). */
  amp: number; // Grundamplitude 0..1
  gustAmp: number; // Böen-Zuschlag 0..1
  lean: number; // Grundneigung in Grad am Stammfuß
  label: string; // "STURM" etc. fürs Mono-Label
}

const LAGEN: Record<WetterLageId, WetterLage> = {
  windstille: { id: "windstille", familie: "calm", amp: 0.08, gustAmp: 0.1, lean: 0, label: "WINDSTILLE" },
  morgentau: { id: "morgentau", familie: "calm", amp: 0.1, gustAmp: 0.12, lean: 0, label: "MORGENTAU" },
  sonnenstrahlen: { id: "sonnenstrahlen", familie: "calm", amp: 0.1, gustAmp: 0.12, lean: 0, label: "SONNIG" },
  leichte_brise: { id: "leichte_brise", familie: "calm", amp: 0.16, gustAmp: 0.2, lean: 0, label: "LEICHTE BRISE" },
  frischer_wind: { id: "frischer_wind", familie: "mild", amp: 0.28, gustAmp: 0.3, lean: 0.2, label: "FRISCHER WIND" },
  vogelflug: { id: "vogelflug", familie: "mild", amp: 0.24, gustAmp: 0.25, lean: 0.2, label: "VOGELFLUG" },
  wolkenschatten: { id: "wolkenschatten", familie: "mild", amp: 0.26, gustAmp: 0.28, lean: 0.2, label: "WOLKENSCHATTEN" },
  falter: { id: "falter", familie: "mild", amp: 0.22, gustAmp: 0.24, lean: 0.1, label: "FALTER" },
  boeig: { id: "boeig", familie: "auffrischend", amp: 0.42, gustAmp: 0.55, lean: 0.5, label: "BÖIG" },
  erste_tropfen: { id: "erste_tropfen", familie: "auffrischend", amp: 0.4, gustAmp: 0.5, lean: 0.5, label: "ERSTE TROPFEN" },
  nebelbank: { id: "nebelbank", familie: "auffrischend", amp: 0.35, gustAmp: 0.4, lean: 0.4, label: "NEBELBANK" },
  blaetterwirbel: { id: "blaetterwirbel", familie: "auffrischend", amp: 0.45, gustAmp: 0.55, lean: 0.5, label: "BLÄTTERWIRBEL" },
  sturm: { id: "sturm", familie: "sturm", amp: 0.7, gustAmp: 0.9, lean: 1.2, label: "STURM" },
  platzregen: { id: "platzregen", familie: "sturm", amp: 0.65, gustAmp: 0.85, lean: 1.2, label: "PLATZREGEN" },
  gewitterleuchten: { id: "gewitterleuchten", familie: "sturm", amp: 0.68, gustAmp: 0.88, lean: 1.2, label: "GEWITTER" },
  orkan: { id: "orkan", familie: "orkan", amp: 1.0, gustAmp: 1.0, lean: 2.5, label: "ORKAN" },
};

/** Kandidaten je Familie in Spec-Reihenfolge (Tages-Seed rotiert). */
const FAMILIE_LAGEN: Record<WetterFamilie, WetterLageId[]> = {
  calm: ["windstille", "morgentau", "sonnenstrahlen", "leichte_brise"],
  mild: ["frischer_wind", "vogelflug", "wolkenschatten", "falter"],
  auffrischend: ["boeig", "erste_tropfen", "nebelbank", "blaetterwirbel"],
  sturm: ["sturm", "platzregen", "gewitterleuchten"],
  orkan: ["orkan"],
};

export interface DeriveLageInput {
  wetter: WetterScore;
  /** YYYYMMDD-Seed (Abwechslung bei Gleichstand). */
  tagesSeed: number;
  /** true wenn kritische Anomalien offen sind (→ Gewitterleuchten). */
  kritischeAnomalien?: boolean;
  /** Debug-Override (?wetter=<id>), sonst undefined. */
  override?: string | null;
}

export function deriveWetterLage(input: DeriveLageInput): WetterLage {
  if (input.override && input.override in LAGEN) {
    return LAGEN[input.override as WetterLageId];
  }
  const { familie, dominanterFaktor, faktoren } = input.wetter;

  // Dominanter Faktor wählt die Signatur-Lage der Familie.
  if (familie === "auffrischend" && dominanterFaktor === "sicht" && faktoren.sicht > 0) {
    return LAGEN.nebelbank;
  }
  if (familie === "auffrischend" && dominanterFaktor === "liquiditaet") {
    return LAGEN.erste_tropfen;
  }
  if (familie === "sturm") {
    if (input.kritischeAnomalien) return LAGEN.gewitterleuchten;
    if (dominanterFaktor === "liquiditaet") return LAGEN.platzregen;
    return LAGEN.sturm;
  }
  if (familie === "orkan") return LAGEN.orkan;

  // Gleichstand/kein Signatur-Treffer: Tages-Seed rotiert durch die Familie.
  const kandidaten = FAMILIE_LAGEN[familie];
  return LAGEN[kandidaten[input.tagesSeed % kandidaten.length]];
}

// ----------------------------------------------------------------------
// Jahreszeit (Kalender) — Saison bestimmt LAUB, Lage bestimmt WIND.
// ----------------------------------------------------------------------

export type Saison = "fruehling" | "sommer" | "herbst" | "winter";

export function saisonFor(monat: number): Saison {
  if (monat >= 3 && monat <= 5) return "fruehling";
  if (monat >= 6 && monat <= 8) return "sommer";
  if (monat >= 9 && monat <= 11) return "herbst";
  return "winter";
}

/** Zitate-Fallback, gekoppelt an die Wetter-Familie (Sturm → Standhaftigkeit,
 *  Calm → Beständigkeit). Kuratierte Client-Liste; der Daemon-Morgenlauf
 *  kann später via state/fokus.json überschreiben. */
const ZITATE: Record<WetterFamilie, string[]> = {
  calm: [
    "Der Baum, der ruhig steht, wächst trotzdem. — nach Seneca",
    "Beständigkeit ist die Mutter aller Meisterschaft. — Marc Aurel (frei)",
    "Wer jeden Tag einen Stein setzt, baut ohne Eile ein Haus. — Baumeister-Weisheit",
    "Stille ist kein Stillstand. — Zen-Sprichwort",
    "Gerade in ruhiger See lernt man das Schiff kennen. — Seemanns-Weisheit",
    "Was langsam reift, hält lange. — Winzer-Spruch",
    "Ordne den Tag, bevor der Tag dich ordnet. — nach Marc Aurel",
    "Der ruhige Bauer schaut nach den Wurzeln, nicht nach dem Wetter. — Bauernregel (frei)",
    "Reichtum ist nicht, viel zu haben, sondern wenig zu brauchen. — Epiktet",
    "Ein festes Fundament macht keinen Lärm. — Baumeister-Weisheit",
  ],
  mild: [
    "Nutze den günstigen Wind, aber halte das Ruder selbst. — nach Seneca",
    "Gelegenheiten multiplizieren sich, wenn man sie ergreift. — Sun Tsu (frei)",
    "Ein guter Tag ist ein Ziegel im Fundament. — Baumeister-Weisheit",
    "Glück ist, wenn Vorbereitung auf Gelegenheit trifft. — nach Seneca",
    "Säe im Guten, damit du im Schlechten erntest. — Bauernregel (frei)",
    "Wer den Hafen kennt, für den ist jeder Wind günstig. — nach Seneca",
    "Kleine Gewinne, oft wiederholt, werden zum Wall. — Kaufmanns-Weisheit",
    "Setze das Segel, solange die Brise trägt. — Seemanns-Weisheit",
  ],
  auffrischend: [
    "Prüfe die Taue, bevor der Wind sie prüft. — Seemanns-Weisheit",
    "Was dich fordert, formt dich. — Epiktet (frei)",
    "Der kluge Baumeister deckt das Dach vor dem Regen. — Baumeister-Weisheit",
    "Nicht der Wind entscheidet, sondern wie du die Segel stellst. — Seemanns-Spruch",
    "Sorge in guten Tagen für die schlechten. — Kaufmanns-Weisheit",
    "Wer die Böe kommen sieht, reeft rechtzeitig. — Seemanns-Weisheit",
    "Der Weitblick spart, was die Eile kostet. — nach Seneca",
    "Ein Reservoir gräbt man vor der Dürre. — Bauernregel (frei)",
  ],
  sturm: [
    "Der Sturm zeigt, welche Wurzeln halten. — nach Seneca",
    "Du hast Macht über deinen Geist, nicht über die Ereignisse. — Marc Aurel",
    "Feste Bauten erkennt man im Unwetter. — Baumeister-Weisheit",
    "Im Sturm rudert man nicht gegen die Welle, man reitet sie. — Seemanns-Weisheit",
    "Ruhe ist die Antwort auf den Lärm der Lage. — nach Epiktet",
    "Handle bei dem, was in deiner Hand liegt; den Rest trage. — Epiktet",
    "Ein Kapitän wird im ruhigen Wasser nicht gemacht. — Seemanns-Spruch",
  ],
  orkan: [
    "Das Hindernis auf dem Weg wird zum Weg. — Marc Aurel",
    "Ein ruhiger Geist ist die stärkste Festung. — Epiktet (frei)",
    "Wenn du durch die Hölle gehst, geh weiter. — Sprichwort",
    "Auch der längste Orkan endet; die Wurzel bleibt. — Bauernregel (frei)",
    "Rette zuerst das Schiff, dann die Ladung. — Seemanns-Weisheit",
    "Der erste Schritt aus der Not ist, sie klar zu sehen. — nach Seneca",
  ],
};

export function zitatFor(familie: WetterFamilie, tagesSeed: number): string {
  const liste = ZITATE[familie];
  return liste[tagesSeed % liste.length];
}

/** Rotiert EINEN Abschnitt des Autosuggestion-Volltexts (seed = YYYYMMDD). */
export function abschnittFor(volltext: string, tagesSeed: number): string {
  const abschnitte = volltext
    .split(/\n\s*\n|(?<=[.!?])\s+(?=[A-ZÄÖÜ])/)
    .map((a) => a.trim())
    .filter((a) => a.length > 20);
  if (abschnitte.length === 0) return volltext.trim();
  return abschnitte[tagesSeed % abschnitte.length];
}

export function tagesSeed(d: Date): number {
  return d.getFullYear() * 10_000 + (d.getMonth() + 1) * 100 + d.getDate();
}
